import UPDATE_ORGANIZATION_ACCESS from '@features/organizationAccess/graphql/mutations/updateOrganizationAccess';
import { useMutation, useQuery } from '@apollo/client';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { type FC } from 'react';
import GET_ACCESS_ORGANIZATION_FILTER from 'src/graphql/querys/access/getAccessOrganizationsFilter';
import { useModalStatusMessage } from '@/hooks';
import GET_DEVICE_ORGANIZATION_FOR_DGA from '../../graphql/queries/getDeviceOrganizationForDga';

type Props = {
  deviceId: string;
};

const DgaOrganizationAccessWarning: FC<Props> = ({ deviceId }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const {
    data: orgData,
    loading: orgLoading,
    error: orgError,
  } = useQuery(GET_DEVICE_ORGANIZATION_FOR_DGA, {
    variables: { deviceId },
  });

  const { data: accessData, loading: accessLoading } = useQuery(GET_ACCESS_ORGANIZATION_FILTER);

  const [updateAccess, { loading: updateLoading }] = useMutation(UPDATE_ORGANIZATION_ACCESS, {
    onCompleted: () => {
      openModalSuccessMessage(_(msg`Acceso a sección Reportes DGA habilitado para la organización`));
    },
    onError: (error) => {
      openModalErrorMessage(error.message);
    },
    refetchQueries: [{ query: GET_DEVICE_ORGANIZATION_FOR_DGA, variables: { deviceId } }],
  });

  if (orgLoading) return null;

  // Fail-loud: si no se pudo verificar el acceso, avisar en vez de ocultar el warning
  // silenciosamente (lo que daría a entender que el acceso está OK sin haberlo comprobado).
  if (orgError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <Trans>No se pudo verificar el acceso a la sección Reportes DGA de la organización.</Trans>
      </Alert>
    );
  }

  if (!orgData?.device?.profile?.organization) return null;

  const organization = orgData.device.profile.organization;

  let orgAccess: Record<string, boolean> = {};
  try {
    orgAccess = JSON.parse(organization.access ?? '{}');
  } catch {
    orgAccess = {};
  }

  if (orgAccess.dgaReports === true) return null;

  // Esperar a la query de permisos antes de decidir el copy/acción, para evitar
  // un flash de la variante "contacta a un administrador" en usuarios con permiso.
  if (accessLoading) return null;

  const canSetOrgAccess =
    accessData?.authorization.authorizationGeneral?.setOrganizacionAccess ?? false;

  const handleEnableAccess = () => {
    const updatedAccess = { ...orgAccess, dgaReports: true };
    updateAccess({
      variables: {
        organizationId: organization.id,
        access: JSON.stringify(updatedAccess),
      },
    });
  };

  return (
    <Alert
      severity="warning"
      sx={{ mt: 2 }}
      action={
        canSetOrgAccess ? (
          <Button
            color="inherit"
            size="small"
            onClick={handleEnableAccess}
            disabled={updateLoading}
            startIcon={updateLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            <Trans>Habilitar</Trans>
          </Button>
        ) : undefined
      }
    >
      <Trans>
        La organización <strong>{organization.name}</strong> no tiene acceso a la sección Reportes
        DGA.
      </Trans>
      {!canSetOrgAccess && (
        <>
          {' '}
          <Trans>Contacta a un administrador para habilitarla.</Trans>
        </>
      )}
    </Alert>
  );
};

export default DgaOrganizationAccessWarning;
